/**
 * MentionDropdown.js
 *
 * 在 NodeConsole 的 prompt 输入框下方弹出的联想浮层。
 *
 * 触发方式：用户在 prompt 中输入 @ 或 / 时，NodeConsole 将光标前的搜索词
 * 传入 `query` prop，本组件从 `assets` 中过滤匹配项并以卡片网格展示。
 *
 * 布局：
 *   repeat(auto-fill, minmax(68px, 1fr)) 网格，宽度由浮层决定每行列数。
 *   图像素材：正方形缩略图 + 左上角分类色彩角标 + 底部单行截断标题。
 *   音频素材：宽扁比例（2.5:1）+ 分类图标，无缩略图。
 *   激活项：分类颜色描边高亮，键盘 ↑↓ 循环导航，Enter 确认，Esc 关闭。
 *
 * 核心设计要点：
 * 1. IME 兼容：键盘事件监听不使用捕获阶段，且判断 e.isComposing / e.keyCode===229
 *    以避免抢占中文输入法的选词框。
 * 2. blob URL 过期处理：资产对象的 localThumbUrl / localCacheUrl 是会话级 blob，
 *    刷新后失效。ThumbImg 组件通过 skipBlob() 跳过，依次 fallback 到
 *    thumbnailUrl → url 确保始终能显示图片。
 * 3. onMouseDown preventDefault：防止点击浮层时 textarea 触发 onBlur，
 *    避免浮层在 onSelect 回调前关闭。
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Music, Film, User, Package, Sparkles, Image, Play, Pause, Loader2 } from 'lucide-react';

// ── 过滤掉 blob: URL ─────────────────────────────────────────────
// 会话级 blob 在页面刷新后失效，必须跳过，否则 <img> 会 404
const skipBlob = (url) => (url && !url.startsWith('blob:')) ? url : null;

// ── 缩略图组件：依次尝试多个 URL 来源，自动跳过失效地址 ──────────
// 优先级：与 NodeConsole 的 ShimmerThumb 保持一致
// localCacheUrl → mini_thumbnail_url → thumbUrl → url
// 跳过 blob: URL（会话级，刷新后失效），只使用持久 URL
const ThumbImg = ({ asset }) => {
  // 优先用标准缩略图（768px），迷你缩略图（80px）太模糊，最后才回退到原图
  const candidates = [
    skipBlob(asset.localCacheUrl),
    asset.thumbUrl,              // 标准缩略图（用于列表显示，清晰）
    asset.thumbnail_url,         // 备用字段名
    asset.mini_thumbnail_url,    // 迷你缩略图（仅用于 Slate 内联气泡）
    asset.url,
  ].filter(Boolean);
  const [srcIdx, setSrcIdx] = useState(0);
  const handleError = useCallback(() => setSrcIdx(i => i + 1), []);
  const src = candidates[srcIdx];
  if (!src) return null;
  return (
    <img
      src={src}
      alt=""
      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      onError={handleError}
    />
  );
};

// ── 分类元数据：分类 ID → { 显示标签, 主题色, 图标组件 } ──────────
// color 同时用于卡片激活描边和角标背景色
const CATEGORY_META = {
  role:    { label: '角色', color: '#a78bfa', Icon: User },
  scene:   { label: '场景', color: '#34d399', Icon: Image },
  prop:    { label: '道具', color: '#fbbf24', Icon: Package },
  motion:  { label: '动作', color: '#60a5fa', Icon: Film },
  vfx:     { label: '特效', color: '#f472b6', Icon: Sparkles },
  music:   { label: '音乐', color: '#fb923c', Icon: Music },
  sfx:     { label: '音效', color: '#fb923c', Icon: Music },
  voice:   { label: '语音', color: '#fb923c', Icon: Music },
  prompt:  { label: '咒语', color: '#818cf8', Icon: Sparkles },
  lora:    { label: 'LoRA', color: '#c084fc', Icon: Sparkles },
  default: { label: '素材', color: '#94a3b8', Icon: Image },
};

// 音频类分类集合，用于区分渲染方式（图标 vs 缩略图）和卡片宽高比
export const AUDIO_CATEGORIES = new Set(['music', 'sfx', 'voice']);

/**
 * MentionDropdown
 *
 * Props:
 *   query      {string}   触发符后的搜索词
 *   assets     {Array}    全量资产列表（已含 localThumbUrl / thumbnailUrl）
 *   onSelect   {Function} (asset) => void  选中后回调
 *   onClose    {Function} () => void       关闭浮层
 *   title      {string}   浮层标题
 *   filterAsset {Function} (asset) => boolean 额外过滤器
 */

// ── 音频卡片播放组件 ────────────────────────────────────────────
const AudioCard = ({ asset, meta, isActive, onSelect, onMouseEnter }) => {
  const audioRef = useRef(null);
  const [playState, setPlayState] = useState('idle'); // idle / loading / playing
  const isPlaying = playState === 'playing';
  const isLoading = playState === 'loading';

  const handlePlayClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying || isLoading) {
      audio.pause();
      setPlayState('idle');
    } else {
      setPlayState('loading');
      audio.play().catch(() => setPlayState('idle'));
    }
  }, [isPlaying, isLoading]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onPlaying = () => setPlayState('playing');
    const onEnded = () => setPlayState('idle');
    const onPause = () => setPlayState(prev => prev === 'loading' ? prev : 'idle');
    audio.addEventListener('playing', onPlaying);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('pause', onPause);
    return () => {
      audio.removeEventListener('playing', onPlaying);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('pause', onPause);
    };
  }, []);

  const audioSrc = asset.localCacheUrl || asset.url;

  return (
    <div
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (onSelect) onSelect(asset);
      }}
      onMouseEnter={onMouseEnter}
      style={{
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 0,
        overflow: 'hidden',
        cursor: 'pointer',
        border: isActive ? `1.5px solid ${meta.color}` : '1.5px solid var(--border-subtle)',
        transition: 'border-color 0.1s',
        flexShrink: 0,
        background: 'transparent',
        boxSizing: 'border-box',
      }}
    >
      <audio ref={audioRef} src={audioSrc} />

      {/* 主体区域：1:1 正方形，跟图片/视频卡片对齐 */}
      <div style={{
        width: '100%',
        aspectRatio: '1 / 1',
        background: '#2d3748',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* 左上角分类标签 */}
        <div style={{
          position: 'absolute',
          top: 3,
          left: 3,
          background: `${meta.color}cc`,
          borderRadius: 0,
          padding: '1px 3px',
          fontSize: '8px',
          color: '#fff',
          lineHeight: 1.2,
          backdropFilter: 'blur(4px)',
        }}>
          {meta.label}
        </div>

        {/* 右上角播放按钮 */}
        <div
          onMouseDown={handlePlayClick}
          onClick={handlePlayClick}
          style={{
            position: 'absolute',
            top: 4,
            right: 4,
            width: 20,
            height: 20,
            borderRadius: 4,
            background: isLoading ? 'rgba(59,130,246,0.25)' : isPlaying ? 'rgba(34,197,94,0.25)' : 'rgba(255,255,255,0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s',
            backdropFilter: 'blur(4px)',
            pointerEvents: 'auto',
          }}
        >
          {isLoading ? (
            <Loader2 size={10} color="#3b82f6" style={{ animation: 'spin 1s linear infinite' }} />
          ) : isPlaying ? (
            <Pause size={10} color="#22c55e" fill="#22c55e" />
          ) : (
            <Play size={10} color="#fff" fill="#fff" />
          )}
        </div>

        {/* 音乐图标 */}
        <meta.Icon size={32} color="rgba(251,146,60,0.8)" strokeWidth={1.5} />
      </div>

      {/* 标题条：底部，跟图片/视频卡片一样 */}
      <div style={{
        padding: '3px 4px 4px',
        fontSize: '10px',
        color: isActive ? 'var(--text-primary)' : 'var(--text-body)',
        fontWeight: isActive ? 600 : 400,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        lineHeight: 1.3,
      }}>
        {asset.title}
      </div>
    </div>
  );
};

const MentionDropdown = ({ query, assets, onSelect, onClose, title = '素材库匹配', filterAsset = null }) => {
  const [activeIdx, setActiveIdx] = useState(0);
  const listRef = useRef(null);

  const getAssetMeta = useCallback((asset) => {
    if (asset?.type === 'text') return CATEGORY_META.prompt;
    return CATEGORY_META[asset?.category] || CATEGORY_META.default;
  }, []);

  // ── 过滤匹配资产（忽略大小写，名字包含 query）────────────────
  const filtered = React.useMemo(() => {
    if (!query && query !== '') return [];
    const q = query.toLowerCase().trim();

    // 定义分类顺序：咒语 → 角色库 → 场景库 → 道具库
    const CATEGORY_ORDER = ['prompt', 'role', 'scene', 'prop'];
    const getCategoryIndex = (asset) => {
      const category = asset?.type === 'text' ? 'prompt' : asset?.category;
      const index = CATEGORY_ORDER.indexOf(category);
      return index >= 0 ? index : 999; // 未知分类排到最后
    };

    return (assets || [])
      .filter(a => {
        if (!a.title) return false;
        if (typeof filterAsset === 'function' && !filterAsset(a)) return false;
        return q === '' || a.title.toLowerCase().includes(q);
      })
      // 按分类顺序排序，每个分类内部保持原顺序（创建时间倒序）
      .sort((a, b) => {
        const catA = getCategoryIndex(a);
        const catB = getCategoryIndex(b);
        return catA - catB;
      })
      // 卡片网格布局，每行自动填充；最多 24 条避免浮层过高
      .slice(0, 24);
  }, [query, assets, filterAsset]);

  // 无匹配时关闭
  useEffect(() => {
    if (filtered.length === 0) onClose?.();
  }, [filtered.length]); // eslint-disable-line

  // 重置高亮
  useEffect(() => { setActiveIdx(0); }, [query]);

  // ── 键盘导航（不拦截 IME 组合期间的按键）────────────────────────
  useEffect(() => {
    const handler = (e) => {
      // IME 组合输入期间不处理，避免抢占选词框
      if (e.isComposing || e.keyCode === 229) return;
      if (!filtered.length) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();
        setActiveIdx(i => (i + 1) % filtered.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        e.stopPropagation();
        setActiveIdx(i => (i - 1 + filtered.length) % filtered.length);
      } else if (e.key === 'Enter') {
        // 只在 dropdown 可见时拦截 Enter
        e.preventDefault();
        e.stopPropagation();
        onSelect?.(filtered[activeIdx]);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose?.();
      }
    };
    // 不使用捕获阶段，让 IME 事件正常优先处理
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [filtered, activeIdx, onSelect, onClose]);

  // 滚动到激活项
  useEffect(() => {
    if (listRef.current) {
      const el = listRef.current.children[activeIdx];
      el?.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIdx]);

  if (!filtered.length) return null;

  // 全部为咒语（text）时，使用与右侧抽屉一致的 flex-wrap 布局，避免 64px 卡片被网格拉伸
  const allText = filtered.every(a => a?.type === 'text');

  return (
    <div
      className="nodrag nowheel"
      style={{
        position: 'absolute',
        top: 'calc(100% + 4px)',
        left: 0,
        right: 0,
        zIndex: 9999,
        background: 'var(--mention-panel)',
        border: '1px solid var(--border-strong)',
        borderRadius: '10px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
        backdropFilter: 'blur(12px)',
        overflow: 'hidden',
      }}
      onMouseDown={(e) => e.preventDefault()}
    >
      {/* 标题行 */}
      <div style={{
        padding: '5px 8px 4px',
        fontSize: '10px',
        color: 'var(--text-secondary)',
        letterSpacing: '0.05em',
        borderBottom: '1px solid var(--border-subtle)',
      }}>
        请选择{title}参考图
      </div>

      {/* 卡片网格 */}
      <div
        ref={listRef}
        style={allText ? {
          // 咒语模式：与右侧抽屉一致的 flex-wrap 布局（卡片固定 64×64）
          display: 'flex',
          flexWrap: 'wrap',
          gap: '6px',
          padding: '6px',
          overflowY: 'auto',
          maxHeight: '220px',
        } : {
          // 卡片网格：auto-fill 自动计算列数（最小 68px/列），间距 5px
          // 结合浮层宽度（left:0 right:0 撑满 prompt 输入框），
          // prompt 越宽每行能显示越多卡片
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(68px, 1fr))',
          gap: '5px',
          padding: '6px',
          overflowY: 'auto',
          maxHeight: '220px',
        }}
        className="custom-scrollbar"
      >
        {filtered.map((asset, idx) => {
          const meta = getAssetMeta(asset);
          const isAudio = AUDIO_CATEGORIES.has(asset.category) || asset.type === 'audio';
          const isTextAsset = asset.type === 'text';
          const isActive = idx === activeIdx;

          // 咒语卡：与右侧抽屉库一致的 64×64 卡片样式（图标+标题居中，无角标无副标题）
          if (isTextAsset) {
            return (
              <div
                key={asset.id || idx}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (onSelect) onSelect(asset);
                }}
                onMouseEnter={() => setActiveIdx(idx)}
                style={{
                  width: '64px',
                  height: '64px',
                  background: 'var(--bg-hover)',
                  border: isActive ? '1px solid #3b82f6' : '1px solid var(--border-subtle)',
                  borderRadius: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'border 0.2s',
                  flexShrink: 0,
                }}
              >
                <div style={{
                  width: '100%',
                  height: '100%',
                  backgroundColor: asset.bgColor || '#2a2a2a',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '6px',
                  boxSizing: 'border-box',
                  gap: '8px',
                }}>
                  <Sparkles size={18} color="#94a3b8" strokeWidth={1.5} />
                  <div style={{
                    fontSize: '10px',
                    color: '#94a3b8',
                    fontWeight: 'bold',
                    textAlign: 'center',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    lineHeight: 1.2,
                    padding: '0 1px',
                  }}>
                    {asset.title || '咒语'}
                  </div>
                </div>
              </div>
            );
          }

          // 音频卡：使用 AudioCard 组件（带播放功能）
          if (isAudio) {
            return (
              <AudioCard
                key={asset.id || idx}
                asset={asset}
                meta={meta}
                isActive={isActive}
                onSelect={onSelect}
                onMouseEnter={() => setActiveIdx(idx)}
              />
            );
          }

          // 图像/视频卡：标准布局（缩略图+左上角分类标签+底部标题）
          return (
            <div
              key={asset.id || idx}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (onSelect) {
                  onSelect(asset);
                }
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (onSelect) {
                  onSelect(asset);
                }
              }}
              onMouseEnter={() => setActiveIdx(idx)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 0,
                overflow: 'hidden',
                cursor: 'pointer',
                border: isActive
                  ? `1.5px solid ${meta.color}`
                  : '1.5px solid var(--border-subtle)',
                background: isActive
                  ? 'rgba(99,102,241,0.12)'
                  : 'var(--bg-hover)',
                transition: 'border-color 0.1s, background 0.1s',
                flexShrink: 0,
              }}
            >
              {/* 缩略图区域：1:1 正方形 */}
              <div style={{
                width: '100%',
                aspectRatio: '1 / 1',
                background: 'var(--bg-hover)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                position: 'relative',
                pointerEvents: 'none',
              }}>
                <ThumbImg asset={asset} />
                {/* 分类角标：左上角绝对定位，颜色取分类主色，追加 cc (80% 不透明度) */}
                <div style={{
                  position: 'absolute',
                  top: 3,
                  left: 3,
                  background: `${meta.color}cc`, // hex + cc = 80% alpha，视觉上半透明贴合背景
                  borderRadius: 0,
                  padding: '1px 3px',
                  fontSize: '8px',
                  color: '#fff',
                  lineHeight: 1.2,
                  backdropFilter: 'blur(4px)',
                }}>
                  {meta.label}
                </div>
              </div>

              {/* 标题 */}
              <div style={{
                padding: '3px 4px 4px',
                fontSize: '10px',
                color: isActive ? 'var(--text-primary)' : 'var(--text-body)',
                fontWeight: isActive ? 600 : 400,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                lineHeight: 1.3,
                pointerEvents: 'none',
              }}>
                {asset.title}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MentionDropdown;
